import { PresignedUrlResponse } from 'api/AnalyzersAPI';
import { initAxios } from '../../../utils/axiosHelper';
import {
  CreateProductCategoryPayload,
  IProduct,
  IProductCategory,
  IProductScreening,
  PaginationParams,
  PaginatedResponse,
} from './products';
import { CreateProductResponse } from './products';

/**
 * Requests a secure, temporary S3 URL to upload the raw ACE document directly from the browser.
 */
export const getProductPresignedUrl = async (
  fileType: string,
  databaseName: string,
  groupId: string,
  teamName: string,
): Promise<PresignedUrlResponse | null> => {
  const instance = await initAxios();
  if (!instance) {
    return null;
  }
  const response = await instance.get('/bite-db/product/presigned-url', {
    params: {
      fileType: fileType,
      databaseName: databaseName,
      groupId: groupId,
      teamName: teamName,
    },
  });
  return response.data;
};

export const getProductById = async (id?: string): Promise<IProduct | null> => {
  try {
    if (!id) {
      return null;
    }
    const instance = await initAxios();
    if (!instance) {
      return null;
    }
    const resp = await instance.get(`/bite-db/product/${id}`);
    return resp.data?.data || null;
  } catch (err) {
    console.error('Error fetching product by ID:', err);
    return null;
  }
};

export const getProductsByDatabaseId = async (
  id?: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<IProduct>> => {
  try {
    if (!id) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
    const instance = await initAxios();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      hierarchy_type: params?.hierarchy_type || '',
      tab: params?.tab || 'All',
      hasIssues: (params?.hasIssues || false).toString(),
    });

    if (Array.isArray(params?.quickFilters)) {
      params?.quickFilters
        .filter((qf) => qf.value === 'true')
        .forEach((filter) => {
          queryParams.append('quickFilters', filter.filter);
        });
    }

    const resp = await instance.get(
      `/bite-db/products/database-id/${id}?${queryParams.toString()}`,
    );
    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching products by database ID:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

export const getProductsByGroupId = async (
  id?: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<IProduct>> => {
  try {
    if (!id) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const instance = await initAxios();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      databaseName: params?.databaseName || '',
      hierarchy_type: params?.hierarchy_type || '',
      hasIssues: (params?.hasIssues || false).toString(),
    });

    if (params?.exclusions && params.exclusions.length > 0) {
      params.exclusions.forEach((exclusion: any) => {
        queryParams.append('exclusions', exclusion);
      });
    }

    const resp = await instance.get(
      `/bite-db/products/group/${id}?${queryParams.toString()}`,
    );

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching products:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

// Bulk delete products
export const deleteBulkProducts = async (
  productIds: string[],
): Promise<{
  successful_deletes: number;
  failed_deletes: number;
  total_requested: number;
}> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }

    const resp = await instance.delete('/bite-db/products-bulk', {
      data: { product_ids: productIds },
    });

    return (
      resp.data?.data || {
        successful_deletes: 0,
        failed_deletes: productIds.length,
        total_requested: productIds.length,
      }
    );
  } catch (err: any) {
    throw new Error(err.response?.data?.message || 'Failed to delete tariffs');
  }
};

export const createProductCategory = async (
  payload: CreateProductCategoryPayload,
): Promise<IProductCategory | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.post('/bite-db/product-category', payload);
    return resp.data?.data || null;
  } catch (err) {
    console.error('Error creating product category:', err);
    return null;
  }
};

export const getProductCategoryById = async (
  id?: string,
): Promise<IProductCategory | null> => {
  try {
    if (!id) {
      return null;
    }

    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/product-category/${id}`);
    return resp.data?.data || null;
  } catch (err) {
    console.error('Error fetching product category by ID:', err);
    return null;
  }
};

export const updateProductCategory = async (
  id: string,
  payload: CreateProductCategoryPayload,
): Promise<IProductCategory | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.put(`/bite-db/product-category/${id}`, payload);
    return resp.data?.data || null;
  } catch (err) {
    console.error('Error updating product category:', err);
    return null;
  }
};

export const deleteProductCategory = async (
  id: string,
): Promise<IProductCategory | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.delete(`/bite-db/product-category/${id}`);
    return resp.data?.data || null;
  } catch (err) {
    console.error('Error deleting product category:', err);
    return null;
  }
};

export const listProductCategories = async (
  groupId?: string,
): Promise<IProductCategory[]> => {
  try {
    if (!groupId) {
      return [];
    }

    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const queryParams = new URLSearchParams({
      groupId: groupId,
    });
    const resp = await instance.get(
      `/bite-db/product-categories?${queryParams.toString()}`,
    );
    return resp.data?.data || [];
  } catch (err) {
    console.error('Error listing product categories:', err);
    return [];
  }
};

export const createProduct = async (
  product: Partial<CreateProductResponse>,
  teamId?: string,
): Promise<CreateProductResponse> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.post(
      '/bite-db/product',
      JSON.stringify(product),
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );
    return resp.data;
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to create product',
    };
  }
};

export const updateProduct = async (
  id: string,
  productData: Partial<IProduct>,
): Promise<{ success: boolean; data?: IProduct; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(
      `/bite-db/product/${id}`,
      JSON.stringify(productData),
    );
    return { success: true, data: resp.data.data };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to update product',
    };
  }
};

export const updateProductStatus = async (
  id: string,
  statusData: { status_flag: string },
): Promise<{ success: boolean; data?: IProduct; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(
      `/bite-db/product-status/${id}`,
      JSON.stringify(statusData),
    );
    return { success: true, data: resp.data.data };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to update product status',
    };
  }
};

export const bulkUploadProducts = async (
  formData: FormData,
): Promise<{ success: boolean; result?: any; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    // Remove content-type headers to let browser set them for FormData
    delete instance.defaults.headers.common['Content-Type'];
    delete instance.defaults.headers.post['Content-Type'];
    delete instance.defaults.headers['Content-Type'];

    const resp = await instance.post(
      '/bite-db/add-product-bulk-upload',
      formData,
      {
        headers: {},
      },
    );
    return { success: true, result: resp.data };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to bulk upload products',
    };
  }
};

// Bulk create products without duplicate check
export const createBulkProductsWithoutDuplicateCheck = async (
  products: Partial<IProduct>[],
  databaseId: string,
  createdBy?: string,
  teamId?: string,
): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    database_id?: string;
    database_product_id?: string;
  }>;
}> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    const payload = {
      queries: products,
      database_id: databaseId,
      created_by: createdBy,
    };

    const resp = await instance.post(
      '/bite-db/products-bulk-no-duplicate-check',
      payload,
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Bulk create products without duplicate check
export const createBulkProductsWithFile = async (
  notMappedColumns: Record<string, string>,
  fileKey: string,
  fileType: string,
  databaseId: string,
  createdBy?: string,
  teamId?: string,
): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    database_id?: string;
    database_product_id?: string;
  }>;
}> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    const payload = {
      s3SignedUrl: fileKey,
      fileType: fileType,
      notMappedColumns: notMappedColumns,
      database_id: databaseId,
      created_by: createdBy,
    };

    const resp = await instance.post('/bite-db/products-bulk-file', payload, {
      headers: {
        'x-group-id': teamId || '',
      },
    });
    return resp.data;
  } catch (err) {
    throw err;
  }
};

export const getProductsByProductId = async (
  productId: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<IProduct>> => {
  try {
    if (!productId) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
    const instance = await initAxios();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      databaseName: params?.databaseName || '',
      hierarchy_type: params?.hierarchy_type || '',
      hasIssues: (params?.hasIssues || false).toString(),
    });

    const resp = await instance.get(
      `/bite-db/products/product/${productId}?${queryParams.toString()}`,
    );

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching products by product ID:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

export const getProductsByHTSCode = async (
  htsCode: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<IProduct>> => {
  try {
    if (!htsCode) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
    const instance = await initAxios();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      databaseName: params?.databaseName || '',
      hierarchy_type: params?.hierarchy_type || '',
      hasIssues: (params?.hasIssues || false).toString(),
    });

    const resp = await instance.get(
      `/bite-db/products/hts-code/${htsCode}?${queryParams.toString()}`,
    );

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching products by HTS code:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

export const getProductScreeningsByProductId = async (
  productId: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<IProductScreening>> => {
  try {
    if (!productId) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
    const instance = await initAxios();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      databaseName: params?.databaseName || '',
    });

    const resp = await instance.get(
      `/bite-db/product-screenings/product/${productId}?${queryParams.toString()}`,
    );

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching screening results by product ID:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

export const getSuppliersByProductId = async (
  productId: string,
): Promise<any[]> => {
  try {
    if (!productId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(
      `/bite-db/supplier-products/product/${productId}`,
    );
    return resp.data.data || [];
  } catch (err) {
    console.error('Error fetching suppliers by product ID:', err);
    return [];
  }
};

export const getProductsBySupplierId = async (
  supplierId: string,
): Promise<any[]> => {
  try {
    if (!supplierId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(
      `/bite-db/supplier-products/supplier/${supplierId}`,
    );
    return resp.data.data || [];
  } catch (err) {
    console.error('Error fetching products by supplier ID:', err);
    return [];
  }
};

export const deleteProduct = async (
  id: string,
): Promise<{ success: boolean; data?: IProduct; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.delete(`/bite-db/product/${id}`);
    return { success: true, data: resp.data.data };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to delete product',
    };
  }
};

// Update entities status from one status to another
export const updateProductsStatusFromTo = async (
  databaseId: string,
  scope: string,
  toStatus: string,
  fromStatus?: string,
  entityIds?: string[],
): Promise<{ success: boolean; message: string; count: number }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }

    const resp = await instance.put(
      `/bite-db/products/database/${databaseId}/status/transition`,
      {
        scope: scope,
        to_status: toStatus,
        from_status: fromStatus,
        product_ids: entityIds,
      },
    );
    return resp.data;
  } catch (err: any) {
    throw new Error(
      err.response?.data?.message || 'Failed to update entities status',
    );
  }
};
